import * as React from 'react';

import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';

import { Button } from 'src/registry/ui/Button';
import { Separator } from 'src/registry/ui/Separator';
import { cn } from 'src/utils/shadcn';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            className={cn(
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-white/80 backdrop-blur-xs',
                className,
            )}
            {...props}
        />
    );
}
const dialogContentVariants = cva(
    'data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex max-h-screen w-full flex-col gap-4 border-gray-200 bg-white p-4 shadow-lg transition ease-in-out sm:gap-6 sm:p-6',
    {
        variants: {
            variant: {
                sheet: 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full border-l data-[state=open]:duration-300 data-[state=open]:ease-in-out sm:max-w-sm',
                leftSheet:
                    'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-[80%] border-r data-[state=open]:duration-300 data-[state=open]:ease-in-out sm:max-w-sm',
                leftSheetStatic:
                    'fixed inset-y-4 left-20 z-30 hidden h-[calc(100vh-2rem)] w-56 flex-col rounded-l-2xl border-y-0 py-4 shadow-none sm:left-20 sm:flex dark:bg-gray-900',
                default:
                    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed left-[50%] top-[50%] w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] border sm:rounded-lg',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

function DialogContent({
    className,
    children,
    variant,
    showCloseButton = true,
    showOverlay = true,
    preventOutsideClose = true,
    onPointerDownOutside,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> &
    VariantProps<typeof dialogContentVariants> & {
        showCloseButton?: boolean;
        showOverlay?: boolean;
        preventOutsideClose?: boolean;
    }) {
    return (
        <DialogPortal data-slot="dialog-portal">
            {showOverlay && <DialogOverlay />}
            <DialogPrimitive.Content
                data-slot="dialog-content"
                className={cn(dialogContentVariants({ variant }), className)}
                onPointerDownOutside={(event) => {
                    if (preventOutsideClose) {
                        event.preventDefault();
                    }
                    onPointerDownOutside?.(event);
                }}
                {...props}
            >
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close
                        asChild
                        data-slot="dialog-close"
                        className="text-muted-foreground hover:text-foreground data-[state=open]:bg-muted"
                    >
                        <Button size="icon" variant="ghost" className="absolute top-3.5 right-3.5">
                            <FontAwesomeIcon icon={faTimes} className="size-4" />
                            <span className="sr-only">Close</span>
                        </Button>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="dialog-header" className={cn('flex flex-col gap-1.5', className)} {...props} />;
}

function DialogHead({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="dialog-head" className={cn('flex flex-col', className)} {...props} />;
}

function DialogSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
    return (
        <Separator
            data-slot="dialog-separator"
            className={cn('-mx-4 data-[orientation=horizontal]:w-auto sm:-mx-6', className)}
            {...props}
        />
    );
}

function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="dialog-body"
            className={cn(
                '-m-4 flex h-full flex-1 flex-col gap-4 overflow-y-auto p-4 sm:-m-6 sm:gap-6 sm:p-6',
                className,
            )}
            {...props}
        />
    );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
            {...props}
        />
    );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn('text-lg leading-none font-semibold', className)}
            {...props}
        />
    );
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn('text-muted-foreground text-sm', className)}
            {...props}
        />
    );
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogHead,
    DialogSeparator,
    DialogBody,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
};
